import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, User, Phone, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';
import InAppBrowserBanner, { detectInAppBrowser } from '../components/InAppBrowserBanner';

const API = `${window.location.origin}/api`;
const LOGO_URL = "/api/files/jsfragrance/products/be3c5fd6-5c5e-4e28-9eca-fed1e5e06eea.png";

function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).filter(Boolean).join(" ");
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState('login');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', password: '', phone: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === 'login') {
        const data = await login(form.email, form.password);
        toast.success(`Welcome back, ${data.name || 'there'}!`);
        if (data.role === 'admin' || data.role === 'super_admin') {
          // Admin may also hold an ambassador profile (dual-role). If they do,
          // prefer the ambassador dashboard when they explicitly used the
          // public /login page (admins usually come in via /admin). They can
          // jump back from the topbar switcher.
          let hasAmbProfile = false;
          try {
            const ambProbe = await axios.get(`${API}/ambassador/me`, {
              headers: { Authorization: `Bearer ${data.token}` },
              validateStatus: () => true,
            });
            hasAmbProfile = ambProbe.status === 200;
          } catch { /* ignore — fall through to admin */ }
          if (hasAmbProfile) {
            // Public-login route → assume they wanted the ambassador surface.
            // Keep the admin token in admin_token slot too so the topbar
            // switcher still recognises them as admin.
            localStorage.setItem('admin_token', data.token);
            navigate('/ambassador');
          } else {
            navigate('/admin/dashboard');
          }
        } else if (data.role === 'ambassador') {
          navigate('/ambassador');
        } else {
          navigate('/account');
        }
      } else {
        if (!form.name.trim()) { toast.error('Name is required'); setLoading(false); return; }
        if (form.password.length < 6) { toast.error('Password must be at least 6 characters'); setLoading(false); return; }
        await register(form.name, form.email, form.password, form.phone);
        toast.success('Account created! Welcome to JS Fragrance');
        navigate('/account');
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
    setLoading(false);
  };

  return (
    <div data-testid="login-page" className="min-h-screen flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="JS Fragrance" className="h-16 w-auto mx-auto mb-4" />
          </Link>
          <h1 className="text-2xl text-white font-light" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            {mode === 'login' ? 'Welcome Back' : 'Create Account'}
          </h1>
          <p className="text-white/40 text-sm mt-1 font-sans">
            {mode === 'login' ? 'Sign in to your JS Fragrance account' : 'Join the JS Fragrance family'}
          </p>
        </div>

        {/* In-app browser warning (Messenger, FB, IG, TikTok…) */}
        <InAppBrowserBanner />

        {/* Google Sign In */}
        <button
          type="button"
          onClick={async () => {
            const inApp = detectInAppBrowser();
            if (inApp) {
              toast.error(`Google blocks sign-in inside ${inApp}. Open this page in Safari or Chrome — see the banner above.`, { duration: 6000 });
              return;
            }
            try {
              const res = await axios.get(`${API}/auth/google/url`);
              window.location.href = res.data.url;
            } catch { toast.error('Google login unavailable'); }
          }}
          className="w-full flex items-center justify-center gap-3 border border-white/20 text-white py-3 mb-4 hover:border-white/40 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="google-login-btn"
        >
          <svg className="w-5 h-5" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/><path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/></svg>
          <span className="text-sm font-sans">Continue with Google</span>
        </button>

        {/* Divider */}
        <div className="flex items-center gap-3 mb-4">
          <div className="flex-1 h-px bg-white/10" />
          <span className="text-white/30 text-xs font-sans uppercase tracking-wider">or</span>
          <div className="flex-1 h-px bg-white/10" />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {mode === 'register' && (
            <>
              <div className="relative">
                <User className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="text"
                  placeholder="Full Name"
                  value={form.name}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 text-white py-3 pl-6 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
                  data-testid="register-name-input"
                  required
                />
              </div>
              <div className="relative">
                <Phone className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                <input
                  type="tel"
                  placeholder="Phone Number (09xx)"
                  value={form.phone}
                  onChange={e => setForm({ ...form, phone: e.target.value })}
                  className="w-full bg-transparent border-b border-white/20 text-white py-3 pl-6 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
                  data-testid="register-phone-input"
                />
              </div>
            </>
          )}

          <div className="relative">
            <Mail className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type="email"
              placeholder="Email"
              value={form.email}
              onChange={e => setForm({ ...form, email: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-3 pl-6 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
              data-testid="login-email-input"
              required
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-0 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder="Password"
              value={form.password}
              onChange={e => setForm({ ...form, password: e.target.value })}
              className="w-full bg-transparent border-b border-white/20 text-white py-3 pl-6 pr-10 text-base focus:border-[#D4AF37] focus:outline-none transition-colors placeholder:text-white/30"
              data-testid="login-password-input"
              required
            />
            <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute right-0 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/60">
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#D4AF37] text-black py-3 text-sm uppercase tracking-[0.15em] font-medium hover:bg-[#F3E5AB] transition-colors disabled:opacity-50 mt-6"
            data-testid="login-submit-btn"
          >
            {loading ? 'Please wait...' : mode === 'login' ? 'Sign In' : 'Create Account'}
          </button>
        </form>

        {/* Toggle */}
        <p className="text-center text-white/40 text-sm mt-6 font-sans">
          {mode === 'login' ? (
            <>Don&apos;t have an account? <button onClick={() => setMode('register')} className="text-[#D4AF37] hover:underline" data-testid="switch-to-register">Create one</button></>
          ) : (
            <>Already have an account? <button onClick={() => setMode('login')} className="text-[#D4AF37] hover:underline" data-testid="switch-to-login">Sign in</button></>
          )}
        </p>

        {mode === 'login' && (
          <p className="text-center mt-3">
            <Link to="/forgot-password" className="text-white/30 text-xs hover:text-white/50 transition-colors font-sans" data-testid="forgot-password-link">Forgot your password?</Link>
          </p>
        )}

        <p className="text-center mt-6">
          <Link to="/" className="text-white/30 text-xs hover:text-white/50 transition-colors font-sans">Continue as Guest</Link>
        </p>
      </div>
    </div>
  );
}
